import type { TradingSignal } from '../types'

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

// "Close to expiry" is under a day, or the last quarter of the signal's life
// if that is shorter. A flat day would flag every 1h signal from the moment
// it opens, since 8 one-hour candles is only 8h.
const SOON_FRACTION = 0.25

export type ExpiryState = 'ok' | 'soon' | 'expired'

export interface SignalExpiry {
  state: ExpiryState
  remainingMs: number
  expiresAt: Date
  label: string
}

/**
 * Parse an API timestamp, reading a zone-less one as UTC.
 *
 * The backend stamps expiry with utcnow(), and some rows serialise without an
 * offset. `new Date()` reads those as local time, which shifts the countdown
 * by the viewer's UTC offset.
 */
export const parseApiDate = (value: string): Date =>
  new Date(/(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`)

/** 4d, 3d 5h, 6h 20m, 45m */
export const formatRemaining = (ms: number): string => {
  if (ms >= DAY_MS) {
    const days = Math.floor(ms / DAY_MS)
    const hours = Math.floor((ms % DAY_MS) / HOUR_MS)
    return hours ? `${days}d ${hours}h` : `${days}d`
  }
  const hours = Math.floor(ms / HOUR_MS)
  const minutes = Math.floor((ms % HOUR_MS) / MINUTE_MS)
  return hours > 0 ? `${hours}h ${minutes}m` : `${Math.max(minutes, 1)}m`
}

/**
 * Time left before the backend auto-cancels this signal.
 *
 * Reads `expires_at`, which the backend sets at creation to created_at +
 * SIGNAL_EXPIRY_CANDLES candles of the signal's timeframe, and which the
 * status-update task cancels against. Recomputing it here would duplicate the
 * config and the timeframe table, and could disagree with what actually
 * happens. A signal with no expires_at never auto-cancels, so it gets none.
 */
export const signalExpiry = (
  signal: Pick<TradingSignal, 'created_at' | 'expires_at'>,
  now: number
): SignalExpiry | null => {
  if (!signal.expires_at) return null
  const expiresAt = parseApiDate(signal.expires_at)
  if (Number.isNaN(expiresAt.getTime())) return null

  const remainingMs = expiresAt.getTime() - now
  if (remainingMs <= 0) {
    return { state: 'expired', remainingMs, expiresAt, label: 'expired' }
  }

  const lifetimeMs = expiresAt.getTime() - parseApiDate(signal.created_at).getTime()
  const soonMs = lifetimeMs > 0 ? Math.min(DAY_MS, lifetimeMs * SOON_FRACTION) : DAY_MS

  return {
    state: remainingMs < soonMs ? 'soon' : 'ok',
    remainingMs,
    expiresAt,
    label: `expires in ${formatRemaining(remainingMs)}`,
  }
}
